// src/components/InstitutionForm.js
import React, { useEffect, useMemo, useCallback } from "react";
import { Form, Image } from "semantic-ui-react";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { initialValues, validationSchema } from "./InstitutionForm.form";
import { InstitutionApi } from "../../../../api/userInstitution";
import { useAuth } from "../../../../hooks";

const institutionApi = new InstitutionApi();

export function InstitutionForm({ close, onReload, user, institution, plans }) {
  const { t } = useTranslation(["institution", "common"]);
  const { accessToken } = useAuth();

  // dropdown planes
  const planOptions = useMemo(
    () =>
      (plans || []).map((p) => ({
        key: p._id,
        value: p._id,
        text: p.title,
        pacientesN: p.patientsNumber,
        terapeutasN: p.therapistsNumber,
      })),
    [plans],
  );

  const formik = useFormik({
    initialValues: initialValues(user, institution, !institution),
    validationSchema: validationSchema(institution),
    validateOnChange: false,
    onSubmit: async (vals) => {
      try {
        if (!institution) {
          await institutionApi.createInstitution(accessToken, vals);
        } else {
          if (vals.fileAvatar && institution.avatar) {
            await institutionApi.deleteAvatar(accessToken, institution.avatar);
          }
          await institutionApi.updateInstitution(
            accessToken,
            institution._id,
            vals,
          );
        }
        onReload();
        close();
      } catch (err) {
        console.error(err);
      }
    },
  });

  // Dropzone avatar
  const onDrop = useCallback(
    (files) => {
      const file = files[0];
      if (file && ["image/jpeg", "image/png"].includes(file.type)) {
        formik.setFieldValue("avatar", URL.createObjectURL(file));
        formik.setFieldValue("fileAvatar", file);
      }
    },
    [formik],
  );
  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/jpeg": [".jpeg", ".jpg"], "image/png": [".png"] },
    onDrop,
    multiple: false,
  });

  // auto-llenar licencias al elegir plan.
  // Extraemos setFieldValue y planEducaId para que el array de deps sea
  // autoconsistente y exhaustive-deps no proteste. Anadir el objeto formik
  // entero al array provocaria bucle infinito (se recrea cada render).
  const { setFieldValue } = formik;
  const { planEducaId } = formik.values;
  useEffect(() => {
    if (!institution && planEducaId) {
      const sel = planOptions.find((o) => o.value === planEducaId);
      if (sel) {
        setFieldValue("pacientesN", sel.pacientesN);
        setFieldValue("terapeutasN", sel.terapeutasN);
      }
    }
  }, [planEducaId, planOptions, institution, setFieldValue]);

  const getAvatar = () =>
    formik.values.fileAvatar
      ? formik.values.avatar
      : institution?.avatar || "/img/no-avatar.png";

  return (
    <Form onSubmit={formik.handleSubmit} className="institution-form">
      {/* Avatar */}
      <Form.Field>
        <label>{t("form.logo")}</label>
        <div {...getRootProps()} className="institution-form__avatar">
          <input {...getInputProps()} />
          <Image avatar size="small" src={getAvatar()} />
        </div>
      </Form.Field>

      {/* Partner */}
      <Form.Field>
        <label>{t("form.partnerId")}</label>
        <span>{formik.values.partnerId}</span>
      </Form.Field>

      {/* Campos */}
      <Form.Field
        control={Form.Input}
        label={t("form.name")}
        name="institutionName"
        placeholder={t("form.namePlaceholder")}
        onChange={formik.handleChange}
        value={formik.values.institutionName}
        error={formik.errors.institutionName}
      />

      <Form.Field
        control={Form.Input}
        label={t("form.address")}
        name="address"
        placeholder={t("form.addressPlaceholder")}
        onChange={formik.handleChange}
        value={formik.values.address}
        error={formik.errors.address}
      />

      <Form.Group widths="equal">
        <Form.Input
          label={t("form.city")}
          name="city"
          placeholder={t("form.cityPlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.city}
          error={formik.errors.city}
        />
        <Form.Input
          label={t("form.country")}
          name="country"
          placeholder={t("form.countryPlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.country}
          error={formik.errors.country}
        />
      </Form.Group>

      <Form.Field
        control={Form.Input}
        label={t("form.website")}
        name="website"
        placeholder="https://..."
        onChange={formik.handleChange}
        value={formik.values.website}
        error={formik.errors.website}
      />

      <Form.Group widths="equal">
        <Form.Input
          label={t("common:fields.phone")}
          name="phone"
          placeholder="600123123"
          onChange={formik.handleChange}
          value={formik.values.phone}
          error={formik.errors.phone}
        />

        <Form.Select
          label={t("form.plan")}
          name="planEducaId"
          options={planOptions}
          placeholder={t("form.planPlaceholder")}
          onChange={(_, data) =>
            formik.setFieldValue("planEducaId", data.value)
          }
          value={formik.values.planEducaId}
          error={formik.errors.planEducaId}
        />
      </Form.Group>

      <Form.Group widths="equal">
        <Form.Input
          label={t("form.patientLicenses")}
          name="pacientesN"
          value={formik.values.pacientesN}
          disabled
        />
        <Form.Input
          label={t("form.therapistLicenses")}
          name="terapeutasN"
          value={formik.values.terapeutasN}
          disabled
        />
      </Form.Group>

      {/* Datos partner-user */}
      <Form.Group widths="equal">
        <Form.Input
          label={t("form.contactName")}
          name="firstname"
          onChange={formik.handleChange}
          value={formik.values.firstname}
          error={formik.errors.firstname}
        />
        <Form.Input
          label={t("common:fields.surname")}
          name="lastname"
          onChange={formik.handleChange}
          value={formik.values.lastname}
          error={formik.errors.lastname}
        />
      </Form.Group>

      <Form.Field
        control={Form.Input}
        label={t("form.email")}
        name="email"
        onChange={formik.handleChange}
        value={formik.values.email}
        error={formik.errors.email}
      />

      {!institution && (
        <Form.Field
          control={Form.Input}
          label={t("common:fields.password")}
          name="password"
          type="password"
          onChange={formik.handleChange}
          value={formik.values.password}
          error={formik.errors.password}
        />
      )}

      <Form.Button primary fluid type="submit">
        {institution ? t("form.submitUpdate") : t("form.submitCreate")}
      </Form.Button>
    </Form>
  );
}
